import React, { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
// ===== Button =====
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
}
const buttonVariants: Record<ButtonVariant, string> = {
  primary: 'bg-indigo-600 hover:bg-indigo-500 text-white border border-transparent',
  secondary: 'bg-slate-700 hover:bg-slate-600 text-slate-100 border border-transparent',
  ghost: 'bg-transparent hover:bg-slate-800 text-slate-300 border border-transparent',
  danger: 'bg-red-600 hover:bg-red-500 text-white border border-transparent',
  outline: 'bg-transparent hover:bg-slate-800 text-slate-200 border border-slate-600'
};
export function Button({
  variant = 'primary',
  size = 'md',
  className,
  children,
  ...props
}: ButtonProps) {
  return <button className={twMerge('inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed', size === 'sm' ? 'px-3 py-1.5 text-sm' : 'px-4 py-2 text-sm', buttonVariants[variant], className)} {...props} data-id="element-226">
      {children}
    </button>;
}
// ===== Badge =====
type BadgeTone = 'slate' | 'green' | 'red' | 'amber' | 'indigo' | 'blue' | 'purple';
const badgeTones: Record<BadgeTone, string> = {
  slate: 'bg-slate-700/60 text-slate-300 border-slate-600/50',
  green: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  red: 'bg-red-500/15 text-red-300 border-red-500/30',
  amber: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  indigo: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30',
  blue: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  purple: 'bg-purple-500/15 text-purple-300 border-purple-500/30'
};
export function Badge({
  tone = 'slate',
  className,
  children
}: {
  tone?: BadgeTone;
  className?: string;
  children: React.ReactNode;
}) {
  return <span className={twMerge('inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium', badgeTones[tone], className)} data-id="element-227">
      {children}
    </span>;
}
// ===== Input / Textarea / Select / Label =====
export function Label({
  children,
  htmlFor,
  required
}: {
  children: React.ReactNode;
  htmlFor?: string;
  required?: boolean;
}) {
  return <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-slate-300" data-id="element-228">
      {children}
      {required && <span className="ml-0.5 text-red-400" data-id="element-229">*</span>}
    </label>;
}
export const Input = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  ...props
}, ref) => <input ref={ref} className={twMerge('w-full rounded-lg border border-slate-600 bg-slate-800/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500', className)} {...props} data-id="element-230" />);
Input.displayName = 'Input';
export function Textarea({
  className,
  ...props
}: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={twMerge('w-full rounded-lg border border-slate-600 bg-slate-800/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500', className)} {...props} data-id="element-231" />;
}
export function Select({
  className,
  children,
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={twMerge('w-full rounded-lg border border-slate-600 bg-slate-800/60 px-3 py-2 text-sm text-slate-100 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500', className)} {...props} data-id="element-232">
      {children}
    </select>;
}
// ===== Card =====
export function Card({
  className,
  children
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return <div className={twMerge('rounded-xl border border-slate-800 bg-slate-900/60', className)} data-id="element-233">
      {children}
    </div>;
}